import { InfoCircleOutlined } from '@ant-design/icons'
import { Switch, theme, Tooltip, Typography } from 'antd'
import React from 'react'
import { useOepToggles } from '../hooks/useOepToggles'
import { OEP_MASTER_KEY } from '../types'
import type { OepToggleKey } from '../types'

const { Title, Text } = Typography

interface OepToggleItem {
  readonly key: OepToggleKey
  readonly label: string
  readonly tooltip: string
}

const TOGGLE_ITEMS: readonly OepToggleItem[] = [
  {
    key: 'showOpenEnrollmentTab',
    label: 'Show Open Enrollment Tab',
    tooltip:
      'Controls visibility of the Open Enrollment tab for this customer. Turning this off hides the Plans and People sub-tabs.',
  },
  {
    key: 'showPlansSubTab',
    label: 'Show Plans Sub-Tab',
    tooltip: 'Controls visibility of the Plans sub-tab within Open Enrollment.',
  },
  {
    key: 'showPeopleSubTab',
    label: 'Show People Sub-Tab',
    tooltip:
      'Controls visibility of the People sub-tab within Open Enrollment.',
  },
]

interface Props {
  readonly clientId: string
}

export function OepToggleSection({ clientId }: Props) {
  const { token } = theme.useToken()
  const { toggles, isMasterOn, isLoading, isError, handleChange } =
    useOepToggles(clientId)

  return (
    <div>
      <Title level={5} style={{ marginBottom: 4 }}>
        Open Enrollment Preview Settings
      </Title>

      <div style={{ marginBottom: 16 }}>
        <Text
          strong
          style={{
            fontSize: token.fontSize,
            display: 'block',
            marginBottom: 4,
          }}
        >
          Settings
        </Text>
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {isError
            ? 'Could not load Open Enrollment settings. Please refresh.'
            : 'Configure the Open Enrollment preview settings for this customer.'}
        </Text>
      </div>

      <div
        style={{ display: 'flex', flexWrap: 'wrap', rowGap: 16, columnGap: 24 }}
      >
        {TOGGLE_ITEMS.map(({ key, label, tooltip }) => {
          const isMaster = key === OEP_MASTER_KEY
          // Dependents are gated by the master: shown OFF + disabled while master is off.
          const checked = isMaster ? toggles[key] : isMasterOn && toggles[key]
          const isDisabled = isLoading || isError || (!isMaster && !isMasterOn)
          return (
            <div
              key={key}
              style={{
                flex: '1 1 240px',
                minWidth: 240,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Switch
                checked={checked}
                disabled={isDisabled}
                loading={isLoading}
                size="default"
                onChange={(value) => handleChange(key, value)}
              />
              <Text
                style={{
                  fontSize: token.fontSize,
                  whiteSpace: 'nowrap',
                  color: isDisabled ? token.colorTextDisabled : undefined,
                }}
              >
                {label}
              </Text>
              <Tooltip title={tooltip}>
                <InfoCircleOutlined
                  style={{
                    fontSize: token.fontSize,
                    color: token.colorTextTertiary,
                    cursor: 'default',
                  }}
                />
              </Tooltip>
              <Text
                strong
                style={{
                  fontSize: token.fontSizeSM,
                  color: checked ? token.colorPrimary : token.colorTextTertiary,
                }}
              >
                {checked ? 'ON' : 'OFF'}
              </Text>
            </div>
          )
        })}
      </div>
    </div>
  )
}
